import { flow, types, getSnapshot, getParent } from "mobx-state-tree"
import Api from '../api/Api'
import State, {notLoaded, pending, error, done} from './State'
import _ from 'lodash'
import validate from 'validate.js'

const CustomerUser = types.model({
    username: types.string,
    id: types.number,
    role: types.number,
    created_at: types.string,
    deleteState: types.optional(State, notLoaded)
})
.actions((self)=>({
    delete: flow(function* _delete() {
        self.deleteState = notLoaded;
        var parent = getParent(self)
        try {
            yield Api.delete(`/manage_customers/${parent.id}/users/${self.id}/`)
            self.deleteState = done
            parent.users = parent.users.filter(user=>user.id !== self.id)
        } catch (e) {
            console.error(e)
            self.deleteState = error
        }
    })
}))

/**
 * This model is returned from list method
 */
const CustomerList = types.model({
    name: types.string,
    id: types.number,
    inventory_count: types.number,
    inventory_weight: types.number,
    total_capacity: types.number, 
    inventory_locations_count: types.number,
})

/**
 * Base model for both Customer and NewCustomer. Contains some common properties and actions.
 */
const BaseCustomer = types.model({
    name: types.string,
    cross_customer_transactions_allowed: types.boolean,
    creating_terminal_locations_allowed: types.boolean,
    feedback_recipients: types.array(types.string),
})
.actions((self)=>({
    addRecipient: (recipient)=>{
        self.feedback_recipients.push(recipient);
    },
    removeRecipient: (recipient)=>{
        self.feedback_recipients = _.filter(self.feedback_recipients, (_recipient)=>_recipient !== recipient);
    },
    setCrossCustomerTransactionsAllowed: (allowed)=>{
        self.cross_customer_transactions_allowed = allowed
    },
    setCreatingTerminalLocationsAllowed: (allowed)=>{
        self.creating_terminal_locations_allowed = allowed
    },
    setName: (name)=>{
        self.name = name;
    }
}))
.views(self=>({
    get errors(){
        var constraints = {
            name: {
              presence: {message: 'Name is missing', allowEmpty: false},
            },
          };
        return validate(self, constraints, {fullMessages: false})
    },
}))

/**
 * Compose Customer from BaseCustomer and CustomerList. Also add some additional properties and actions.
 */
const Customer = types.compose(
    BaseCustomer,
    CustomerList,
    types.model({
        id: types.number,
        users: types.array(CustomerUser),
        account: types.frozen(),
        saveState: types.optional(State, notLoaded),
        archiveState: types.optional(State, notLoaded),
        createUserState: types.optional(State, notLoaded),
        createUserError: types.frozen()
    })
    .actions((self)=>({
        save: flow(function* update() {
            self.saveState = pending
            try {
                var snapshot = getSnapshot(self);
                var data = _.pick(snapshot, [
                    'name',
                    'feedback_recipients', 
                    'creating_terminal_locations_allowed', 
                    'cross_customer_transactions_allowed', 
                ]);
                
                yield Api.patch(`/manage_customers/${snapshot.id}/`, data)
                self.saveState = done
            } catch (e) {
                console.error(e)
                self.saveState = error
            }
        }),
        archive: flow(function* update() {
            self.archiveState = pending
            try {
                var snapshot = getSnapshot(self);
                
                yield Api.post(`/manage_customers/${snapshot.id}/archive/`)
                self.archiveState = done
            } catch (e) {
                console.error(e)
                self.archiveState = error
            }
        }),
        createUser: flow(function* createUser(username) {
            self.createUserState = pending
            self.createUserError = null;
            try {
                var data = {
                    username,
                    role: 1
                }
                const response = yield Api.post(`/manage_customers/${self.id}/users/`, data)
                self.users.push({
                    username: username,
                    id: response.data.id,
                    role: data.role
                });
                self.createUserState = done
            } catch (e) {
                console.error(e)
                self.createUserState = error
                self.createUserError = e
            }
        })
    }))
)
/**
 * This is used to create new Customers
 */
const NewCustomer = types.compose(
    BaseCustomer,
    types.model({
        name: types.optional(types.string, ''),
        account: types.maybe(types.number),
        cross_customer_transactions_allowed: types.optional(types.boolean, false),
        creating_terminal_locations_allowed: types.optional(types.boolean, false) ,
        feedback_recipients: types.optional(types.array(types.string), []),
        saveState: types.optional(State, notLoaded),
        saveResult: types.frozen()
    })
    .actions((self)=>({
        save: flow(function* update() {
            self.saveState = pending
            try {         
                var snapshot = getSnapshot(self);
                var data = _.pick(snapshot, [
                    'name',
                    'feedback_recipients', 
                    'creating_terminal_locations_allowed', 
                    'cross_customer_transactions_allowed', 
                    'account'
                ]);
                
                const response = yield Api.post(`/manage_customers/`, data)
                self.saveResult = response.data;
                self.saveState = done
            } catch (e) {
                console.error(e)
                self.saveState = error
            }
        })
    }))
)

const CustomerStore = types.model({
    customers: types.maybe(types.array(CustomerList)),
    customersState: types.optional(State, pending),
    customer: types.maybe(Customer),
    customerState: types.optional(State, pending),
    newCustomer: types.maybe(NewCustomer),
    createCustomerUserResult: types.frozen(),
    createCustomerUserState: types.optional(State, notLoaded),
    deleteCustomerResult: types.frozen(),
    deleteCustomerState: types.optional(State, notLoaded)
})
.actions(self => ({
    createCustomer:(accountId)=>{
        self.newCustomer = NewCustomer.create({account: accountId});
    },
    fetchCustomers: flow(function* fetchCustomers(showArchived) {
        self.customers = undefined
        self.customersState = pending
        try {
            let path = '/manage_customers/';
            if (!showArchived) {
                path = `${path}?archived_at__isnull=true`
            }

            const response = yield Api.get(path)
            self.customers = response.data
            self.customersState = done
        } catch (e) {
            console.error(e)
            self.customersState = error
        }
    }),
    fetchAccountCustomers: flow(function* fetchAccountCustomers(accountId, showArchived) {
        self.customers = undefined
        self.customersState = pending
        try {
            let path = `/manage_customers/?account=${accountId}`;
            if (!showArchived) {
                path = `${path}&archived_at__isnull=true`
            }
            const response = yield Api.get(path)
            self.customers = response.data
            self.customersState = done
        } catch (e) {
            console.error(e)
            self.customersState = error
        }
    }),
    fetchCustomer: flow(function* fetchCustomer(id) {
        self.customerState = pending
        try {
            const response = yield Api.get(`/manage_customers/${id}/`)
            self.customer = response.data
            self.customerState = done
        } catch (e) {
            console.error(e)
            self.customerState = error
        }
    })
}))

export default CustomerStore.create({})


// WEBPACK FOOTER //
// ./src/stores/CustomerStore.js